import { create, StateCreator } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { v4 as uuidv4 } from 'uuid'
import { genSaltSync } from "bcrypt-ts"
import type {
   PrefStoreState,
   TarifCurrencies,
   CatalogMainAlign,
   GlobalClosePopups,
   Lang,
   DateFormat,
   TimeFormat,
   ImgSourcePrio,
   RetailRole,
   Theme,
} from "../types"

// ─── Conditional compilation (vite-plugin-conditional-compile) ───────────────
// https://github.com/KeJunMao/vite-plugin-conditional-compile

let _pwaApiEndpoint!: string
let _appWriteEndpoint!: string
let _appWriteProjectId!: string
let _appWriteDatabaseId!: string
let _appWriteCollCustomersId!: string
let _appWriteCollBiOrdersId!: string
let _appWriteCollOrdersId!: string
let _appWriteCollPDFId!: string
let _appWriteCollUdidId!: string
let _appWriteCollListsId!: string
let _appWriteCollStockId!: string
let _appWriteCollOtpId!: string
let _appWriteCollTarifsId!: string
let _appWriteCollVentesRALId!: string
let _appWriteCollConfiesId!: string
let _appWriteCollConfiesRALId!: string

// #v-ifdef VITE_MODE=prod
_appWriteEndpoint = "https://rbv3-api.messikagroup.com/v1"
_pwaApiEndpoint = "https://rbv3-webhooks.messikagroup.com"
_appWriteProjectId = "ome"
_appWriteDatabaseId = "production"
_appWriteCollCustomersId = "customers"
_appWriteCollBiOrdersId = "orders_bi"
_appWriteCollOrdersId = "orders"
_appWriteCollPDFId = "pdf"
_appWriteCollUdidId = "udids"
_appWriteCollListsId = "lists"
_appWriteCollStockId = "stocks"
_appWriteCollOtpId = "otp"
_appWriteCollTarifsId = "tarifs"
_appWriteCollVentesRALId = "ventes_ral"
_appWriteCollConfiesId = "confies"
_appWriteCollConfiesRALId = "confies_ral"
// #v-endif
// #v-ifdef VITE_MODE=beta
_appWriteEndpoint = "https://rbv3-dev-api.messikagroup.com/v1"
_pwaApiEndpoint = "https://rbv3-dev-webhooks.messikagroup.com"
_appWriteProjectId = "ome"
_appWriteDatabaseId = "production"
_appWriteCollCustomersId = "customers"
_appWriteCollBiOrdersId = "orders_bi"
_appWriteCollOrdersId = "orders"
_appWriteCollPDFId = "pdf"
_appWriteCollUdidId = "udids"
_appWriteCollListsId = "lists"
_appWriteCollStockId = "stocks"
_appWriteCollOtpId = "otp"
_appWriteCollTarifsId = "tarifs"
_appWriteCollVentesRALId = "ventes_ral"
_appWriteCollConfiesId = "confies"
_appWriteCollConfiesRALId = "confies_ral"
// #v-endif
// #v-ifdef VITE_MODE=dev
_appWriteEndpoint = "http://localhost/v1"
_pwaApiEndpoint = "http://localhost:3300"
_appWriteProjectId = "ome"
_appWriteDatabaseId = "production"
_appWriteCollCustomersId = "customers"
_appWriteCollBiOrdersId = "orders_bi"
_appWriteCollOrdersId = "orders"
_appWriteCollPDFId = "pdf"
_appWriteCollUdidId = "udids"
_appWriteCollListsId = "lists"
_appWriteCollStockId = "stocks"
_appWriteCollOtpId = "otp"
_appWriteCollTarifsId = "tarifs"
_appWriteCollVentesRALId = "ventes_ral"
_appWriteCollConfiesId = "confies"
_appWriteCollConfiesRALId = "confies_ral"
// _appWriteProjectId = "644ff20fd4f45a14ed27"
// _appWriteDatabaseId = "644ff28ea6c7b48f05e5"
// _appWriteCollCustomersId = "64516f930d0fe5b14ae7"
// _appWriteCollBiOrdersId = "647f0e33e27154620eb8"
// _appWriteCollOrdersId = "64b49518aed39efabfaf"
// _appWriteCollPDFId = "649789413cabb9b2c0d9"
// _appWriteCollUdidId = "649e0af54adcdbae5007"
// _appWriteCollListsId = "649ef43ece51e4579328"
// _appWriteCollStockId = "649868218df758e91bf7"
// _appWriteCollOtpId = "otp"
// _appWriteCollTarifsId = "649cc8cc388ac8c022de"
// _appWriteCollVentesRALId = "ventes_ral"
// _appWriteCollConfiesId = "confies"
// _appWriteCollConfiesRALId = "confies_ral"
// #v-endif

// ─── Types ───────────────────────────────────────────────────────────────────

/** Valeur numérique passée depuis un <input> — peut être string ou number */
type NumericInput = number | string

interface PrefStoreActions {
   /** Patch libre de l'état (pour migration / fix) */
   fix: (val: Partial<PrefStoreState>) => void
   /** Retourne une copie de l'état initial (sans appeler set) */
   initialState: () => PrefStoreState
   /** Réinitialise l'état en conservant udid, rememberMe, bcryptSalt et endpoints.
    * Si `opts.regenerateUdid` est true, un nouvel udid est généré (utile en dev
    * pour éviter les collisions d'IDs côté AppWrite quand on teste sur un poste
    * dont l'udid existe déjà). */
   reset: (opts?: { regenerateUdid?: boolean }) => void
   /** Réinitialise l'état depuis un objet injecté (récupération serveur) */
   recover: (new_state: Partial<PrefStoreState>) => void

   setIsLogged: (val: boolean) => void
   setSessionDuration: (val: number) => void
   setCurTimeout: (val: number | null) => void
   setRememberMe: (val: boolean) => void
   setPwaApiEndpoint: (val: string) => void
   setAppWriteEndpoint: (val: string) => void
   setAppWriteDatabaseId: (val: string) => void
   setAppWriteProjectId: (val: string) => void
   setLogin: (val: string) => void
   setUserName: (val: string) => void
   setHashedPassword: (val: string | null) => void
   setHashedLastLogin: (val: string | null) => void
   setUdid: (val: string) => void
   setUdidPrefixChecked: (val: boolean) => void
   setLocalOrderId: (val: NumericInput) => void
   setCatalogMainAlign: (val: CatalogMainAlign) => void
   setGlobalClosePopups: (val: GlobalClosePopups) => void
   setTheme: (val: Theme) => void
   setShowThemeToggle: (val: boolean) => void
   setShowTests: (val: boolean) => void
   setDefaultLang: (val: Lang) => void
   setQtyMaxOrder: (val: NumericInput) => void
   setMaxTarifsOffline: (val: NumericInput) => void
   setShowWeeksNum: (val: boolean) => void
   setCurrentLang: (val: Lang) => void
   setMissingTarifRegionName: (val: string | null) => void
   setCurTarifRegionName: (val: string | null) => void
   setCurTarifRegion: (val: TarifCurrencies | null) => void
   setCurTarifCustomer: (val: TarifCurrencies | null) => void
   setCurCustomer: (val: string | null) => void
   setCurOrder: (val: string | null) => void
   setDebug: (val: boolean) => void
   setScreenshotMode: (val: boolean) => void
   setMinFilterLength: (val: NumericInput) => void
   setNoScrollbars: (val: boolean) => void
   setImgSourcePrio: (val: ImgSourcePrio) => void
   setTimeFormat: (val: TimeFormat) => void
   setDateFormat: (val: DateFormat) => void
   setListPageLength: (val: NumericInput) => void
   setMaxPageButtonsOnLists: (val: NumericInput) => void
   setShowImageSource: (val: boolean) => void
   setFormatPhone: (val: boolean) => void
   setSearchModeInOrder: (val: string | null) => void
   setSearchModeInCustomer: (val: string | null) => void
   setRecentCustomers: (val: string[] | null) => void
   setRecentOrders: (val: string[] | null) => void
   setMaxRecentItems: (val: NumericInput) => void
   setShowCess: (val: boolean) => void
   setShowCessDelay: (val: NumericInput) => void
   setKeepHistoryOpen: (val: boolean) => void
   setAppVerForLastCachePurge: (val: string | null) => void
   setIsGrandUtilisateur: (val: boolean) => void
   setRetailRole: (val: RetailRole) => void
   setServerRetailRole: (val: RetailRole) => void
   setIsRetailManager: (val: boolean) => void
   setSupervisedN1: (val: string[]) => void
   setSupervisedN2: (val: string[]) => void
   setAbsence: (from: string | null, to: string | null) => void
   setPrefsLoaded: (val: boolean) => void
   setImpersonation: (target: string | null, operator: string | null) => void
   setPwaCanal: (val: number) => void
}

type PrefStore = PrefStoreState & PrefStoreActions

// ─── État initial ─────────────────────────────────────────────────────────────

const initialState: PrefStoreState = {
   udid: uuidv4(),
   udidPrefixChecked: false,

   pwaApiEndpoint: _pwaApiEndpoint,
   appWriteEndpoint: _appWriteEndpoint,
   appWriteProjectId: _appWriteProjectId,
   appWriteDatabaseId: _appWriteDatabaseId,
   appWriteCollCustomersId: _appWriteCollCustomersId,
   appWriteCollBiOrdersId: _appWriteCollBiOrdersId,
   appWriteCollOrdersId: _appWriteCollOrdersId,
   appWriteCollPDFId: _appWriteCollPDFId,
   appWriteCollUdidId: _appWriteCollUdidId,
   appWriteCollListsId: _appWriteCollListsId,
   appWriteCollStockId: _appWriteCollStockId,
   appWriteCollOtpId: _appWriteCollOtpId,
   appWriteCollTarifsId: _appWriteCollTarifsId,
   appWriteCollVentesRALId: _appWriteCollVentesRALId,
   appWriteCollConfiesId: _appWriteCollConfiesId,
   appWriteCollConfiesRALId: _appWriteCollConfiesRALId,

   isLogged: false,
   sessionDuration: 3600,           // 1H
   curTimeout: null,                // Heure de timeout
   rememberMe: false,
   login: "",
   userName: "",                    // Nom du user tel que défini dans l'auth Appwrite (account.get().name)
   bcryptSalt: genSaltSync(10),
   hashedLastLogin: null,           // Hash du dernier login utilisé avec succès
   hashedPassword: null,            // Hash password pour login offline
   localOrderId: "1",               // ID local de la prochaine commande (préfixé des 4 prem. chiffres du UDID)
   catalogMainAlign: "left",        // Vignettes du catalogue à gauche (left) ou centrées (center)
   globalClosePopups: "button",     // "button" = uniquement la croix
   theme: "default",                // Thème de l'app : "default" | "alt"
   showThemeToggle: false,          // Afficher le toggle de thème global (fixed top-left)
   showTests: false,                // Afficher l'item "Tests" dans le menu dashboard
   currentLang: "fr",
   defaultLang: "fr",
   showWeeksNum: true,              // Affichage des numéros de semaine dans le date picker
   qtyMaxOrder: 999,                // Qté maxi pour une ligne de commande
   maxTarifsOffline: 6,             // Nb maxi de tarifs préchargeables pour le mode Offline
   curTarifRegionName: null,        // Nom du tarif (région) courant
   missingTarifRegionName: null,    // Nom du tarif choisi mais absent — doit être chargé
   curTarifRegion: null,            // Tarif (région) courant
   curTarifCustomer: null,          // Tarif (client) courant
   curCustomer: null,               // ID du client actif
   curOrder: null,                  // ID de la commande active
   debug: false,                    // Affichage des erreurs de débogage
   screenshotMode: false,           // Mode captures : debug reste actif (rôle retail conservé) mais le bloc debug du Menu est masqué
   minFilterLength: 3,              // Longueur mini pour déclencher un filtrage
   noScrollbars: false,             // Toujours masquer les ascenseurs
   imgSourcePrio: 'internal',       // Priorité source images : internal | bymessika
   timeFormat: '24',                // Format heure : 12 | 24
   dateFormat: 'dmy',               // Format date : dmy | ymd | mdy | ydm
   listPageLength: 100,             // Taille des listes en nb de lignes / page (défaut 100)
   maxPageButtonsOnLists: 5,        // Nb maxi de boutons de pages en pied des listes
   showImageSource: false,          // Affichage puce grise pour byMessika
   formatPhone: true,               // Formatage des numéros de téléphone (format International)
   searchModeInOrder: null,         // Mode recherche depuis un BdC
   searchModeInCustomer: null,      // Mode recherche depuis un détail client
   recentCustomers: [],             // Derniers clients consultés (IDs)
   recentOrders: [],                // Dernières commandes consultées (IDs)
   maxRecentItems: 5,               // Nb maxi de "derniers items"
   showCess: true,                  // Affichage des prix de Cession
   showCessDelay: 2000,             // Délai d'appui pour rétablir l'affichage des prix de Cession
   keepHistoryOpen: true,           // Garder historique ouvert en changeant de page
   appVerForLastCachePurge: null,   // Version pour laquelle on a purgé le cache la dernière fois
   isGrandUtilisateur: false,       // Grand Utilisateur (accès étendu)
   retailRole: null,                // Rôle Retail effectif = miroir de serverRetailRole (lu depuis user.prefs au login)
   serverRetailRole: null as RetailRole,  // Rôle assigné par l'admin (lu depuis user.prefs au login)
   isRetailManager: false,          // user.prefs.retail_manager (autorise la déclaration d'absence depuis Params)
   prefsLoaded: false,              // True une fois que les prefs serveur ont été lues (au login OU à la restauration de session). Permet aux écrans de différencier "pas encore connu" vs "explicitement null" pour les rôles retail.
   impersonateTarget: null as string | null,    // messika-pwa#10 : email du compte impersoné (posé avant le login opérateur)
   impersonateOperator: null as string | null,  // email de l'opérateur, posé une fois la vérification OK (= impersonation ACTIVE)
   supervisedN1: [] as string[],    // customer_ids supervisés en N1 par ce manager (dénorm depuis user-clients)
   supervisedN2: [] as string[],    // customer_ids supervisés en N2 par ce manager (dénorm depuis user-clients)
   absenceFrom: null,               // Période d'absence déclarée (YYYY-MM-DD)
   absenceTo: null,
   pwaCanal: 1,                     // Canal PWA voulu (pref pwa_canal ; opérateur en impersonation)
}

// ─── Store ────────────────────────────────────────────────────────────────────

const prefStore: StateCreator<PrefStore> = (set) => ({
   ...initialState,

   fix: (val) =>
      set((state) => {
         console.log('>>>', val, { ...val })
         return { ...val }
      }),

   initialState: () => ({      // Pour réinitialiser l'état par défaut
      ...initialState
   }),

   reset: (opts) =>            // Pour réinitialiser l'état par défaut
      set((state) => ({
         ...initialState,
         // udid : conservé par défaut, ou régénéré si demandé (cas dev où l'udid
         // local entre en conflit avec un autre poste — préfixe identique sur les
         // IDs de commandes).
         udid: opts?.regenerateUdid ? uuidv4() : (state.udid ?? uuidv4()),
         // Unicité du préfixe : à revérifier si on régénère l'udid (dev), sinon préservée.
         udidPrefixChecked: opts?.regenerateUdid ? false : (state.udidPrefixChecked ?? false),
         rememberMe: state.rememberMe ?? true,             // le rememberMe
         // Compteur d'ID de commande : device-scoped comme l'udid, il NE doit jamais régresser
         // (sinon réutilisation d'IDs déjà créés sur AW → createDocument 409 + 401). Cf. bug reliquat.
         localOrderId: state.localOrderId ?? initialState.localOrderId,
         bcryptSalt: state.bcryptSalt ?? genSaltSync(10),  // la clé de hashage
         pwaApiEndpoint: state.pwaApiEndpoint,             // les endPoints
         appWriteEndpoint: state.appWriteEndpoint,
         debug: state.debug,                               // on conserve l'affichage mode debug/expert à travers la purge
         // Identité de SESSION (posée par doLogin / restauration) : un reset de données ne doit
         // PAS l'effacer. Sinon le cleanDb(true) lancé APRÈS doLogin lors d'un changement de user
         // (Login.tsx onLogin) ré-écrase serverRetailRole='client' en null → "Rôle serveur: aucun"
         // jusqu'au refresh. Cf. bug login serveur=aucun.
         isGrandUtilisateur: state.isGrandUtilisateur,
         serverRetailRole: state.serverRetailRole,
         retailRole: state.retailRole,
         isRetailManager: state.isRetailManager,
         supervisedN1: state.supervisedN1,
         supervisedN2: state.supervisedN2,
         prefsLoaded: state.prefsLoaded,
         // Impersonation : jamais effacée par un reset de données (purge d'abord, mémorisation ensuite).
         impersonateTarget: state.impersonateTarget,
         impersonateOperator: state.impersonateOperator,
         pwaCanal: state.pwaCanal,                         // canal voulu : identité de session, jamais purgé
      })),

   recover: (new_state) =>     // Pour réinitialiser l'état par injection depuis le serveur
      set(() => ({
         ...initialState, ...new_state
      })),

   setIsLogged: (val) =>
      set(() => ({ isLogged: val })),

   setSessionDuration: (val) =>
      set(() => ({ sessionDuration: val })),

   setCurTimeout: (val) =>
      set(() => ({ curTimeout: val })),

   setRememberMe: (val) =>
      set(() => ({ rememberMe: val })),

   setPwaApiEndpoint: (val) =>
      set(() => ({ pwaApiEndpoint: val })),

   setAppWriteEndpoint: (val) =>
      set(() => ({ appWriteEndpoint: val })),

   setAppWriteDatabaseId: (val) =>
      set(() => ({ appWriteDatabaseId: val })),

   setAppWriteProjectId: (val) =>
      set(() => ({ appWriteProjectId: val })),

   setLogin: (val) =>
      set(() => ({ login: val })),
   setUserName: (val) =>
      set(() => ({ userName: val })),

   setHashedPassword: (val) =>
      set(() => ({ hashedPassword: val })),

   setHashedLastLogin: (val) =>
      set(() => ({ hashedLastLogin: val })),

   setUdid: (val) =>
      set(() => ({ udid: val })),

   setUdidPrefixChecked: (val) =>
      set(() => ({ udidPrefixChecked: val })),

   setLocalOrderId: (val) =>
      set(() => ({ localOrderId: String(val) })),

   setCatalogMainAlign: (val) =>
      set(() => ({ catalogMainAlign: val })),

   setGlobalClosePopups: (val) =>
      set(() => ({ globalClosePopups: val })),

   setTheme: (val) =>
      set(() => ({ theme: val })),

   setShowThemeToggle: (val) =>
      set(() => ({ showThemeToggle: val })),

   setShowTests: (val) =>
      set(() => ({ showTests: val })),

   setDefaultLang: (val) =>
      set(() => ({ defaultLang: val })),

   setQtyMaxOrder: (val) =>
      set(() => ({ qtyMaxOrder: Number(val) })),

   setMaxTarifsOffline: (val) =>
      set(() => ({ maxTarifsOffline: Number(val) })),

   setShowWeeksNum: (val) =>
      set(() => ({ showWeeksNum: val })),

   setCurrentLang: (val) =>
      set(() => ({ currentLang: val })),

   setMissingTarifRegionName: (val) =>
      set(() => ({ missingTarifRegionName: val })),

   setCurTarifRegionName: (val) =>
      set(() => ({ curTarifRegionName: val })),

   setCurTarifRegion: (val) =>
      set(() => ({ curTarifRegion: val })),

   setCurTarifCustomer: (val) =>
      set(() => ({ curTarifCustomer: val })),

   setCurCustomer: (val) =>
      set(() => ({ curCustomer: val })),

   setCurOrder: (val) =>
      set(() => ({ curOrder: val })),

   setDebug: (val) =>
      set(() => ({ debug: val })),
   setScreenshotMode: (val) =>
      set(() => ({ screenshotMode: val })),

   setMinFilterLength: (val) => {
      const len = Number(val)
      return set(() => ({ minFilterLength: len <= 0 ? 2 : len }))
   },

   setNoScrollbars: (val) =>
      set(() => ({ noScrollbars: val })),

   setImgSourcePrio: (val) =>
      set(() => ({ imgSourcePrio: val })),

   setTimeFormat: (val) =>
      set(() => ({ timeFormat: val })),

   setDateFormat: (val) =>
      set(() => ({ dateFormat: val })),

   setListPageLength: (val) => {
      const len = Number(val)
      return set(() => ({ listPageLength: len <= 0 ? 10 : len }))
   },

   setMaxPageButtonsOnLists: (val) => {
      const num = Number(val)
      return set(() => ({ maxPageButtonsOnLists: num <= 0 ? 5 : num }))
   },

   setShowImageSource: (val) =>
      set(() => ({ showImageSource: val })),

   setFormatPhone: (val) =>
      set(() => ({ formatPhone: val })),

   setSearchModeInOrder: (val) =>
      set(() => ({ searchModeInOrder: val })),

   setSearchModeInCustomer: (val) =>
      set(() => ({ searchModeInCustomer: val })),

   setRecentCustomers: (val) =>
      set(() => ({ recentCustomers: val ?? [] })),      // Évite de stocker des erreurs

   setRecentOrders: (val) => {
      console.log('setRecentOrders', val)
      set(() => ({ recentOrders: val ?? [] }))          // Évite de stocker des erreurs
   },

   setMaxRecentItems: (val) => {
      const num = Number(val)
      return set(() => ({ maxRecentItems: num <= 0 ? 5 : num }))
   },

   setShowCess: (val) =>
      set(() => ({ showCess: val })),

   setShowCessDelay: (val) =>
      set(() => ({ showCessDelay: Number(val) })),

   setKeepHistoryOpen: (val) =>
      set(() => ({ keepHistoryOpen: val })),

   setAppVerForLastCachePurge: (val) =>
      set(() => ({ appVerForLastCachePurge: val })),

   setIsGrandUtilisateur: (val) =>
      set(() => ({ isGrandUtilisateur: val })),

   setRetailRole: (val) =>
      set(() => ({ retailRole: val })),

   setServerRetailRole: (val) =>
      // retailRole est désormais TOUJOURS le miroir du rôle serveur (plus de surcharge debug).
      set(() => ({ serverRetailRole: val, retailRole: val })),

   setIsRetailManager: (val) =>
      set(() => ({ isRetailManager: val })),

   setSupervisedN1: (val) =>
      set(() => ({ supervisedN1: Array.isArray(val) ? val : [] })),

   setSupervisedN2: (val) =>
      set(() => ({ supervisedN2: Array.isArray(val) ? val : [] })),

   setAbsence: (from, to) =>
      set(() => ({ absenceFrom: from || null, absenceTo: to || null })),

   setPrefsLoaded: (val) =>
      set(() => ({ prefsLoaded: val })),

   setImpersonation: (target, operator) =>
      set(() => ({ impersonateTarget: target, impersonateOperator: operator })),

   setPwaCanal: (val) =>
      set(() => ({ pwaCanal: val })),
})

const usePrefStore = create<PrefStore>()(devtools(persist(prefStore, {
   name: "prefs",
   partialize: (state) => {
      // retailRole est le miroir de serverRetailRole → on ne le persiste pas (re-posé à l'hydration).
      const { retailRole, ...rest } = state
      return rest
   },
   onRehydrateStorage: () => (state) => {
      // À l'hydration : retailRole = rôle serveur (plus aucune surcharge debug).
      if (!state) return
      state.retailRole = state.serverRetailRole
   },
})))

/**
 * Helper : attend que l'hydration Zustand (lecture IndexedDB) soit terminée.
 * Indispensable avant d'écrire dans le store depuis du code "rapide" (doLogin),
 * sinon l'hydration tardive écrase les valeurs fraîchement posées.
 */
export function awaitPrefsHydrated(): Promise<void> {
   if (usePrefStore.persist.hasHydrated()) return Promise.resolve()
   return new Promise<void>((resolve) => {
      const unsub = usePrefStore.persist.onFinishHydration(() => {
         unsub()
         resolve()
      })
   })
}

export default usePrefStore
export type { PrefStore, PrefStoreActions }
